<script>
	//@ts-nocheck
	import {
		ArrowRight,
		Building,
		Castle,
		Footprints,
		House,
		MountainSnow,
		PawPrint,
		Sailboat,
		Ship,
		Snowflake,
		Trees,
		Waves
	} from 'lucide-svelte';
	import { fly } from 'svelte/transition';
	import * as Accordion from '$lib/components/ui/accordion';
	import { enhancedImages } from '$lib/utils/images';
	import allTours from '$lib/data/allTours.json';

	const validTours = Array.isArray(allTours) ? allTours : [];

	function isEnhancedImage(imageUrl) {
		if (!imageUrl || imageUrl.includes('http://') || imageUrl.includes('https://')) return false;
		return !!Object.entries(enhancedImages).find(([key]) => key === imageUrl);
	}

	function getImageSource(imageUrl) {
		const entry = Object.entries(enhancedImages).find(([key]) => key === imageUrl);
		if (entry?.[1]?.default) return entry[1].default;
		return imageUrl;
	}

	function formatPrice(price) {
		return `From $${price.toFixed(2)}`;
	}

	function formatDuration(durationMinutes) {
		const hours = Math.ceil(durationMinutes / 60);
		if (hours >= 24) {
			const days = Math.ceil(hours / 24);
			return `${days} day${days > 1 ? 's' : ''}`;
		}
		return `${hours} hour${hours > 1 ? 's' : ''}`;
	}

	function formatProductType(productType) {
		const labels = {
			DAYTOUR: 'Day Tour',
			MULTIDAYTOUR: 'Multi-day Tour'
		};
		return labels[productType] || productType;
	}

	function getToursByCode(codes) {
		const tours = codes
			.map((code) => validTours.find((t) => t.productCode === code))
			.filter(Boolean);
		return tours.sort((a, b) => {
			const isDay = (t) => t.productType === 'DAYTOUR' || t.productType === 'DAY TOUR';
			return isDay(b) - isDay(a);
		});
	}

	const regionDefs = [
		{
			title: 'Hobart',
			description:
				"Experience the vibrant culture of Hobart, Tasmania's capital, where historic charm meets modern creativity. From the bustling Salamanca Market to the stunning views atop Mount Wellington, this city offers a unique blend of art, food, and breathtaking natural scenery that captivates every visitor. Our tours cover all of these highlights and extend to the coast of Port Arthur.",
			codes: ['PNVP8M', 'PQ5CJC', 'PQKW4C', 'PMPLBM', 'P6GE12', 'PHKZNS', 'PKQPM9', 'PTNCNK', 'PM5WCG', 'PBRPKL']
		},
		{
			title: "Tasmania's West Coast",
			description:
				"Discover the untamed beauty of Tasmania's West Coast, where ancient rainforests and wild rivers create an exhilarating backdrop for adventure. Whether you're white-water rafting on the Franklin River or hiking through pristine wilderness, this region promises a raw and unforgettable experience in nature's embrace.",
			codes: ['P0V9R2', 'PTWH1T', 'P1VDC1']
		},
		{
			title: "Tasmania's East Coast",
			description:
				"Embrace the serene landscapes of Tasmania's East Coast, renowned for its pristine beaches and stunning coastal views. With opportunities for gourmet seafood tasting, wildlife encounters, and leisurely walks through lush national parks, this region invites you to unwind and savor the simple pleasures of life by the sea.",
			codes: ['PNNK81', 'PHJFAH', 'PVHGUQ', 'P0QKC2', 'PKQPM9', 'PM5WCG', 'PBRPKL', 'P0V9R2', 'PTWH1T', 'P1VDC1']
		},
		{
			title: 'Cradle Mountain',
			description:
				'Cradle Mountain is a breathtaking landmark nestled within the Cradle Mountain-Lake St Clair National Park, renowned for its dramatic peaks and glacial lakes. Visitors can explore a variety of walking tracks, including the famous Overland Track, while encountering diverse wildlife such as Tasmanian devils and wombats in this UNESCO World Heritage site.',
			codes: ['PCBKLS', 'P0QKC2', 'PM5WCG', 'PBRPKL', 'P0V9R2', 'PTWH1T', 'P1VDC1']
		},
		{
			title: 'Mount Field National Park',
			description:
				"Mount Field National Park is celebrated for its stunning landscapes, featuring lush rainforests, cascading waterfalls, and alpine scenery. Home to the iconic Russell Falls and a variety of walking trails, this park offers visitors a chance to immerse themselves in Tasmania's natural beauty, making it a perfect destination for both relaxation and adventure.",
			codes: ['PQ5CJC', 'PU9VRB']
		},
		{
			title: 'Bruny Island',
			description:
				'Discover the natural beauty and gourmet delights of Bruny Island, a paradise just off Hobarts south coast. From stunning coastal walks and wildlife encounters to sampling world-class local produce, Bruny Island offers an unforgettable blend of adventure and indulgence.',
			codes: ['P18ZC5', 'P01GQC', 'PU9VRB', 'P6GE12', 'P1AHF0', 'PHKZNS', 'PKQPM9', 'PTNCNK', 'PM5WCG', 'PBRPKL']
		},
		{
			title: 'Port Arthur',
			description:
				"Step back in time at Port Arthur, Tasmania's most significant convict site and a UNESCO World Heritage location. Explore the well-preserved penal colony ruins and immerse yourself in the haunting history of Australia's colonial past through interactive exhibits and guided tours.",
			codes: ['PJHLE0', 'P1AHF0', 'PHKZNS', 'PTNCNK', 'PM5WCG', 'PBRPKL']
		},
		{
			title: 'Launceston',
			description:
				"Discover the vibrant city of Launceston, where history meets modernity in Tasmania's north. From the stunning Cataract Gorge to the city's thriving food and wine scene, Launceston offers a perfect blend of natural beauty and urban sophistication.",
			codes: ['PCBKLS', 'PHJFAH', 'P0QKC2', 'PVHGUQ', 'P0V9R2']
		},
		{
			title: 'Strahan',
			description:
				"Discover the charming harbor town of Strahan, gateway to Tasmania's rugged west coast wilderness. Embark on a river cruise through the pristine Gordon River, explore the region's convict history, and experience the unique blend of natural beauty and cultural heritage.",
			codes: ['PTWH1T', 'P0V9R2']
		},
		{
			title: 'Wineglass Bay',
			description:
				"Behold the pristine beauty of Wineglass Bay, often ranked among the world's top ten beaches. Located in Freycinet National Park, this stunning crescent of white sand and turquoise water offers unforgettable views and excellent opportunities for hiking, kayaking, and wildlife spotting.",
			codes: ['PNNK81', 'PHJFAH', 'P0QKC2', 'PKQPM9', 'PM5WCG', 'PBRPKL', 'PTWH1T']
		},
		{
			title: 'Hastings Caves',
			description:
				"Venture underground to explore the magnificent Hastings Cave, a vast dolomite cave system in Tasmania's far south. While exploring the Hastings Caves State Reserve, keep your eyes peeled for native wildlife like platypus, quolls, pademelons and birds.",
			codes: ['PTC0KJ', 'PTNCNK', 'PBRPKL', 'PTWH1T']
		}
	];

	const regions = regionDefs.map((r) => ({
		title: r.title,
		description: r.description,
		tours: getToursByCode(r.codes)
	}));

	let current = $state(0);
</script>

<div class="" id="explore">
	<section class="relative mx-auto hidden overflow-hidden bg-[#194139] xl:flex">
		<div class="mx-auto grid h-[700px] max-w-7xl px-4 lg:grid-cols-2">
			{#key current}
				<div
					in:fly={{ x: 100, duration: 300, delay: 300 }}
					out:fly={{ x: -100, duration: 300 }}
					class="flex flex-col justify-center"
				>
					<h2 class="font-heading text-3xl font-semibold text-background lg:text-5xl">
						{regions[current].title}
					</h2>
					<p class="text-md mt-6 text-background lg:leading-7">
						{regions[current].description}
					</p>
					<div class="my-6 h-[360px] overflow-y-scroll">
						<ul class="grid grid-cols-2 gap-3">
							{#each regions[current].tours as tour}
								<li class="group">
									<a
										href={tour.tourPath}
										data-sveltekit-noscroll
										class="relative flex aspect-video flex-col items-start justify-end overflow-hidden rounded-xl bg-gray-900"
									>
										{#if tour.images?.[0] && isEnhancedImage(tour.images[0].itemUrl)}
											<enhanced:img
												src={getImageSource(tour.images[0].itemUrl)}
												alt={tour.name}
												class="absolute inset-0 h-full w-full object-cover"
												sizes="(min-width: 1280px) 260px, 50vw"
												loading="lazy"
											/>
										{:else if tour.images?.[0]?.itemUrl}
											<img
												src={tour.images[0].itemUrl}
												alt={tour.name}
												class="absolute inset-0 h-full w-full object-cover"
												loading="lazy"
											/>
										{/if}
										<div
											class="absolute inset-0 bg-foreground/40 transition-all group-hover:bg-foreground/55"
										></div>
										<div class="relative space-y-0.5 p-3">
											<h4 class="line-clamp-2 text-base font-semibold text-background">
												{tour.name}
											</h4>
											<p class="text-xs font-semibold text-background/80">
												{formatProductType(tour.productType)}
											</p>
											<p class="text-xs text-background/70">
												{formatPrice(tour.advertisedPrice)} • {formatDuration(tour.durationMinutes)}
											</p>
										</div>
										<div
											class="absolute bottom-3 right-3 translate-x-8 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-60"
										>
											<ArrowRight class="h-5 w-5 text-background" />
										</div>
									</a>
								</li>
							{/each}
						</ul>
					</div>
				</div>
			{/key}
		</div>
		<div class="absolute -top-20 right-4 hidden h-[800px] w-auto xl:inline-block">
			<enhanced:img
				src="$lib/assets/images/tas-map.png"
				alt="Map of Tasmania"
				class=" h-[800px] w-auto object-cover"
			/>
		</div>

		<!-- MAP BUTTONS -->

		<!-- hobart -->
		<button
			aria-label="region-select"
			class={current === 0
				? 'absolute bottom-[145px] right-[210px] hidden h-10  w-10 items-center justify-center rounded-full bg-background transition xl:flex'
				: 'absolute bottom-[145px] right-[210px] hidden h-10  w-10 items-center justify-center rounded-full bg-tt-red transition xl:flex'}
			onclick={() => (current = 0)}
			><House
				class={current === 0 ? 'h-5 w-5  transition' : 'h-5 w-5 stroke-background transition'}
			/></button
		>

		<!-- west coast -->
		<button
			aria-label="region-select"
			class={current === 1
				? 'absolute bottom-[280px] right-[575px] hidden h-10  w-10 items-center justify-center rounded-full bg-background transition xl:flex'
				: 'absolute bottom-[280px] right-[575px] hidden h-10  w-10 items-center justify-center rounded-full bg-tt-red transition xl:flex'}
			onclick={() => (current = 1)}
			><Footprints
				class={current === 1 ? 'h-5 w-5  transition' : 'h-5 w-5 stroke-background transition'}
			/></button
		>

		<!-- east coast -->

		<button
			aria-label="region-select"
			class={current === 2
				? ' absolute bottom-[465px] right-[25px] hidden h-10 w-10 items-center justify-center rounded-full bg-background transition xl:flex'
				: ' absolute bottom-[465px] right-[25px] hidden h-10 w-10 items-center justify-center rounded-full bg-tt-red transition xl:flex'}
			onclick={() => (current = 2)}
			><Waves
				class={current === 2 ? 'h-5 w-5  transition' : 'h-5 w-5 stroke-background transition'}
			/></button
		>

		<!-- cradle mountain -->

		<button
			aria-label="region-select"
			class={current === 3
				? 'absolute bottom-[480px] right-[450px] hidden h-10  w-10 items-center justify-center rounded-full bg-background transition xl:flex'
				: 'absolute bottom-[480px] right-[450px] hidden h-10  w-10 items-center justify-center rounded-full bg-tt-red transition xl:flex'}
			onclick={() => (current = 3)}
			><MountainSnow
				class={current === 3 ? 'h-5 w-5  transition' : 'h-5 w-5 stroke-background transition'}
			/></button
		>

		<!-- mt field -->
		<button
			aria-label="region-select"
			class={current === 4
				? 'absolute bottom-[215px] right-[285px] hidden h-10  w-10 items-center justify-center rounded-full bg-background transition xl:flex'
				: 'absolute bottom-[215px] right-[285px] hidden h-10  w-10 items-center justify-center rounded-full bg-tt-red transition xl:flex'}
			onclick={() => (current = 4)}
			><Snowflake
				class={current === 4 ? 'h-5 w-5  transition' : 'h-5 w-5 stroke-background transition'}
			/></button
		>

		<!-- bruny island -->
		<button
			aria-label="region-select"
			class={current === 5
				? 'absolute bottom-[45px] right-[200px] hidden h-10  w-10 items-center justify-center rounded-full bg-background transition xl:flex '
				: 'absolute bottom-[45px] right-[200px] hidden h-10  w-10 items-center justify-center rounded-full bg-tt-red transition xl:flex '}
			onclick={() => (current = 5)}
			><PawPrint
				class={current === 5 ? 'h-5 w-5  transition' : 'h-5 w-5  text-background transition'}
			/></button
		>

		<!-- port arthur -->
		<button
			aria-label="region-select"
			class={current === 6
				? 'absolute bottom-[45px] right-[110px] hidden h-10  w-10 items-center justify-center rounded-full bg-background transition xl:flex '
				: 'absolute bottom-[45px] right-[110px] hidden h-10  w-10 items-center justify-center rounded-full bg-tt-red transition xl:flex '}
			onclick={() => (current = 6)}
			><Castle
				class={current === 6 ? 'h-5 w-5  transition' : 'h-5 w-5  text-background transition'}
			/></button
		>
		<!-- launceston -->
		<button
			aria-label="region-select"
			class={current === 7
				? 'absolute bottom-[490px] right-[250px] hidden h-10  w-10 items-center justify-center rounded-full bg-background transition xl:flex '
				: 'absolute bottom-[490px] right-[250px] hidden h-10  w-10 items-center justify-center rounded-full bg-tt-red transition xl:flex '}
			onclick={() => (current = 7)}
			><Building
				class={current === 7 ? 'h-5 w-5  transition' : 'h-5 w-5  text-background transition'}
			/></button
		>
		<!-- strahan -->
		<button
			aria-label="region-select"
			class={current === 8
				? 'absolute bottom-[360px] right-[620px] hidden h-10  w-10 items-center justify-center rounded-full bg-background transition xl:flex '
				: 'absolute bottom-[360px] right-[620px] hidden h-10  w-10 items-center justify-center rounded-full bg-tt-red transition xl:flex '}
			onclick={() => (current = 8)}
			><Ship
				class={current === 8 ? 'h-5 w-5  transition' : 'h-5 w-5  text-background transition'}
			/></button
		>
		<!-- wineglass bay -->
		<button
			aria-label="region-select"
			class={current === 9
				? 'absolute bottom-[350px] right-[20px] hidden h-10  w-10 items-center justify-center rounded-full bg-background transition xl:flex '
				: 'absolute bottom-[350px] right-[20px] hidden h-10  w-10 items-center justify-center rounded-full bg-tt-red transition xl:flex '}
			onclick={() => (current = 9)}
			><Sailboat
				class={current === 9 ? 'h-5 w-5  transition' : 'h-5 w-5  text-background transition'}
			/></button
		>
		<!-- hastings cave -->
		<button
			aria-label="region-select"
			class={current === 10
				? 'absolute bottom-[10px] right-[325px] hidden h-10  w-10 items-center justify-center rounded-full bg-background transition xl:flex '
				: 'absolute bottom-[10px] right-[325px] hidden h-10  w-10 items-center justify-center rounded-full bg-tt-red transition xl:flex '}
			onclick={() => (current = 10)}
			><Trees
				class={current === 10 ? 'h-5 w-5  transition' : 'h-5 w-5  text-background transition'}
			/></button
		>
	</section>

	<section class="relative mx-auto overflow-hidden bg-[#194139] px-4 py-12 xl:hidden">
		<h2 class="font-heading text-2xl font-semibold text-background lg:text-5xl">
			Explore Tasmania
		</h2>
		<Accordion.Root type="single" class="w-full   py-6 text-background" value="item-0">
			{#each regions as item, index}
				<Accordion.Item value="item-{index}" class="border-background/40">
					<Accordion.Trigger class="text-lg ">{@html item.title}</Accordion.Trigger>
					<Accordion.Content>
						<p class="text-md text-background lg:leading-7">
							{item.description}
						</p>
						<div class="my-6 h-[360px] overflow-y-scroll">
							<ul class="grid grid-cols-1 gap-3">
								{#each item.tours as tour}
									<li class="group">
										<a
											href={tour.tourPath}
											class="relative flex aspect-video flex-col items-start justify-end overflow-hidden rounded-xl bg-gray-900"
										>
											{#if tour.images?.[0] && isEnhancedImage(tour.images[0].itemUrl)}
												<enhanced:img
													src={getImageSource(tour.images[0].itemUrl)}
													alt={tour.name}
													class="absolute inset-0 h-full w-full object-cover"
													sizes="(min-width: 640px) 50vw, 100vw"
													loading="lazy"
												/>
											{:else if tour.images?.[0]?.itemUrl}
												<img
													src={tour.images[0].itemUrl}
													alt={tour.name}
													class="absolute inset-0 h-full w-full object-cover"
													loading="lazy"
												/>
											{/if}
											<div
												class="absolute inset-0 bg-foreground/40 transition-all group-hover:bg-foreground/55"
											></div>
											<div class="relative space-y-0.5 p-3">
												<h4 class="line-clamp-2 text-base font-semibold text-background">
													{tour.name}
												</h4>
												<p class="text-xs font-semibold text-background/80">
													{formatProductType(tour.productType)}
												</p>
												<p class="text-xs text-background/70">
													{formatPrice(tour.advertisedPrice)} • {formatDuration(tour.durationMinutes)}
												</p>
											</div>
											<div
												class="absolute bottom-3 right-3 translate-x-8 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-60"
											>
												<ArrowRight class="h-5 w-5 text-background" />
											</div>
										</a>
									</li>
								{/each}
							</ul>
						</div>
					</Accordion.Content>
				</Accordion.Item>
			{/each}
		</Accordion.Root>
	</section>
</div>
